import axios from "axios";
import { ApiResponse } from "@/types";
import { filterEmptyProps } from "@/helpers/helpers";

export interface GetAllAreasWithStatsParams {
    userId?: string;
    limit?: number;
    page?: number;
    dateTo?: string;
    dateFrom?: string;
    timeZoneOffset: number;
    areaId?: string;
    excludeRecents?: boolean;
}

export interface GetAllRecentAreasParams {
    userId: string;
    limit?: number;
    page?: number;
    dateTo?: string;
    dateFrom?: string;
    timeZoneOffset: number;
}

export interface SubmitAreaRequestParams {
    areaName: string;
    zipCode: string;
}

export interface SearchClosestAreasParams {
    lat: string | number;
    lon: string | number;
    page?: number;
    limit?: number;
    user_id?: string;
}

/**
 * Asynchronously fetches default area data
 *
 * @returns A promise that resolves with the default area data.
 */
export const getDefaultArea = async (): Promise<ApiResponse<any>> => {
    return (await axios.get(`/api/areas/default/area`)).data;
};

/**
 * Asynchronously fetches area data from the server based on the specified area ID.
 *
 * @param areaId - The ID of the area to fetch.
 * @returns A promise that resolves with the area data.
 */
export const getAreaById = async (areaId: string): Promise<ApiResponse<any>> => {
    return (await axios.get(`/api/areas/${areaId}`)).data;
};

/**
 * Asynchronously fetches all areas
 *
 * @returns A promise that resolves with all the areas.
 */
export const getAllAreas = async (): Promise<ApiResponse<any>> => {
    return await axios.get(`/api/areas`);
};

/**
 * Retrieves all areas with statistics from the server.
 *
 * @param params - Object containing parameters for the request.
 * @param params.userId - The ID of the user (optional).
 * @param params.limit - The maximum number of areas to return (optional).
 * @param params.page - The page number for pagination (optional).
 * @param params.dateTo - The end date for filtering (optional).
 * @param params.dateFrom - The start date for filtering (optional).
 * @param params.timeZoneOffset - The timezone offset in minutes.
 * @param params.areaId - The specific area ID to filter by (optional).
 * @param params.excludeRecents - Whether to exclude recent areas (optional).
 * @returns A promise that resolves with areas data including statistics.
 */
export const getAllAreasWithStats = async ({
    userId,
    limit,
    page,
    dateTo,
    dateFrom,
    timeZoneOffset,
    areaId,
    excludeRecents,
}: GetAllAreasWithStatsParams): Promise<ApiResponse<any>> => {
    const payload = {
        user_id: userId,
        limit,
        page,
        dateTo,
        dateFrom,
        timeZoneOffset: Math.abs(timeZoneOffset),
        areaId,
        excludeRecents,
    } as Record<string, any>;

    const filteredPayload = filterEmptyProps(payload);
    const query = new URLSearchParams(filteredPayload).toString();

    return (await axios.get(`/api/areas/all/stats?${query}`)).data;
};

/**
 * Retrieves all recent areas for a specific user from the server.
 *
 * @param params - Object containing parameters for the request.
 * @param params.userId - The ID of the user.
 * @param params.limit - The maximum number of areas to return (optional).
 * @param params.page - The page number for pagination (optional).
 * @param params.dateTo - The end date for filtering (optional).
 * @param params.dateFrom - The start date for filtering (optional).
 * @param params.timeZoneOffset - The timezone offset in minutes.
 * @returns A promise that resolves with recent areas data.
 */
export const getAllRecentAreas = async ({
    userId,
    limit,
    page,
    dateTo,
    dateFrom,
    timeZoneOffset,
}: GetAllRecentAreasParams): Promise<ApiResponse<any>> => {
    const payload = {
        limit,
        page,
        dateTo,
        dateFrom,
        timeZoneOffset: Math.abs(timeZoneOffset),
    } as Record<string, any>;

    const filteredPayload = filterEmptyProps(payload);
    const query = new URLSearchParams(filteredPayload).toString();

    return (await axios.get(`/api/areas/recents/${userId}?${query}`)).data;
};

/**
 * Submits a new area request to the server.
 *
 * @param params - Object containing the area request details.
 * @param params.areaName - The name of the area being requested.
 * @param params.zipCode - The zip code of the area being requested.
 * @returns A promise that resolves with the submission response.
 */
export const submitAreaRequest = async ({ areaName, zipCode }: SubmitAreaRequestParams): Promise<ApiResponse<any>> => {
    return await axios.post("/api/area-requests", {
        name: areaName,
        zipcode: zipCode,
    });
};

/**
 * Searches for the closest areas based on latitude and longitude coordinates.
 *
 * @param params - Object containing search parameters.
 * @param params.lat - The latitude coordinate.
 * @param params.lon - The longitude coordinate.
 * @param params.page - The page number for pagination (default: 1).
 * @param params.limit - The maximum number of areas to return (default: 10).
 * @param params.user_id - The ID of the user (optional).
 * @returns A promise that resolves with the closest areas data.
 */
export const searchClosestAreas = async ({
    lat,
    lon,
    page = 1,
    limit = 10,
    user_id,
}: SearchClosestAreasParams): Promise<ApiResponse<any>> => {
    const query = new URLSearchParams({
        lat: String(lat),
        lng: String(lon),
        page: String(page),
        limit: String(limit),
        user_id: user_id ?? "",
    }).toString();
    return (await axios.get(`/api/areas/closest?${query}`)).data;
};
